import type { StrictOmit } from "ts-essentials";
import { z } from "zod";
import type { SchemaShape, TableSchemaShape } from "~/components/Form";
import {
  boolean,
  commonResourceFilterSchemas,
  filterBoolean,
  filterText,
  filterUuid,
  optionalObject,
  optionalText,
  requiredText,
} from "~/domain/common";
import type {
  HookCreateRequest,
  HookUpdateRequest,
  ListHooksRequest,
  ListWorkflowsRequest,
  WorkflowCreateRequest,
  WorkflowUpdateRequest,
} from "~/lqs";
import {
  filterProcessState,
  filterProcessType,
  requiredProcessStateSchema,
  requiredProcessTypeSchema,
} from "~/lqs";

export const listWorkflowsSchema = z.object({
  id: filterUuid,
  name: filterText,
  nameLike: filterText,
  _default: filterBoolean,
  managed: filterBoolean,
  disabled: filterBoolean,
  ...commonResourceFilterSchemas,
} satisfies TableSchemaShape<
  StrictOmit<ListWorkflowsRequest, "contextSchemaFilter" | "includeCount">
>);

export const createWorkflowSchema = z.object({
  name: requiredText,
  _default: boolean,
  managed: boolean,
  disabled: boolean,
  contextSchema: optionalObject,
  note: optionalText,
  context: optionalObject,
} satisfies SchemaShape<WorkflowCreateRequest>);

export const editWorkflowSchema = z.object({
  name: requiredText,
  _default: boolean,
  disabled: boolean,
  contextSchema: optionalObject,
  note: optionalText,
  context: optionalObject,
} satisfies SchemaShape<WorkflowUpdateRequest>);

export const EDITABLE_WORKFLOW_FIELDS = editWorkflowSchema.keyof().options;

export const listWorkflowHooksSchema = z.object({
  id: filterUuid,
  name: filterText,
  nameLike: filterText,
  uri: filterText,
  uriLike: filterText,
  triggerProcess: filterProcessType,
  triggerState: filterProcessState,
  noteLike: filterText,
  managed: filterBoolean,
  disabled: filterBoolean,
  ...commonResourceFilterSchemas,
} satisfies TableSchemaShape<
  StrictOmit<ListHooksRequest, "workflowId" | "includeCount">
>);

export const createWorkflowHookSchema = z.object({
  name: optionalText,
  triggerProcess: requiredProcessTypeSchema,
  triggerState: requiredProcessStateSchema,
  uri: optionalText,
  secret: optionalText,
  managed: boolean,
  disabled: boolean,
  note: optionalText,
  context: optionalObject,
} satisfies SchemaShape<HookCreateRequest>);

export const editWorkflowHookSchema = z.object({
  name: optionalText,
  triggerProcess: requiredProcessTypeSchema,
  triggerState: requiredProcessStateSchema,
  uri: optionalText,
  disabled: boolean,
  note: optionalText,
  context: optionalObject,
} satisfies SchemaShape<StrictOmit<HookUpdateRequest, "secret">>);

export const EDITABLE_WORKFLOW_HOOK_FIELDS =
  editWorkflowHookSchema.keyof().options;
